import type { SiteLayer, SiteEntity } from '../../types/site';
import type { SiteDocument } from '../../types/siteRag';
import type { SiteEvidenceDocument, EvidenceSection, GridEvidence, FloodEvidence, ParcelEvidence } from '../../types/siteEvidence';
import { computeLayerContentHash } from '../kmzParser';

const DOC_STRUCTURED_CONFIDENCE = 0.6;

function asObject(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
}

function toFiniteNumber(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return value;
}

function toNonEmptyString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function ensureSectionData<T>(
  section: EvidenceSection<T>,
  fallback: T,
): T {
  if (section.data) return section.data;
  section.data = fallback;
  return section.data;
}

function applyDocLink(section: EvidenceSection<unknown>, docId: string): void {
  section.linkedDocumentIds = Array.from(new Set([...(section.linkedDocumentIds ?? []), docId]));
  section.supportingDocIds = Array.from(new Set([...(section.supportingDocIds ?? []), docId]));
  section.confidence = Math.max(section.confidence ?? 0, DOC_STRUCTURED_CONFIDENCE);
  if (section.status === 'unavailable') section.status = 'verified-from-doc';
  section.provenance =
    section.provenance === 'provider' || section.provenance === 'provider+doc'
      ? 'provider+doc'
      : 'doc';
  section.provenanceDetail = {
    ...(section.provenanceDetail ?? {}),
    mergeReason: 'iia-structured-overlay',
    docId,
  };
}

function floodRiskFromZone(zone: string): FloodEvidence['floodRisk'] {
  const z = zone.toUpperCase();
  if (z === 'VE' || z === 'V') return 'High';
  if (z === 'AE' || z === 'AH' || z === 'AO' || z === 'A') return 'Medium';
  return 'Low';
}

function gridCapacityFromMw(capacityMw: number): GridEvidence['capacity'] {
  if (capacityMw >= 500) return 'High';
  if (capacityMw >= 150) return 'Limited';
  return 'Constrained';
}

function siteAnchor(site: Pick<SiteEntity, 'location' | 'siteBoundary' | 'siteCentroid'>): { lat: number; lng: number } | null {
  if (site.siteCentroid && Number.isFinite(site.siteCentroid.lat) && Number.isFinite(site.siteCentroid.lng)) {
    return site.siteCentroid;
  }
  if (site.location && Number.isFinite(site.location.lat) && Number.isFinite(site.location.lng)) {
    return site.location;
  }
  const boundary = site.siteBoundary;
  if (boundary && boundary.length > 0) {
    const lat = boundary.reduce((sum, p) => sum + p.lat, 0) / boundary.length;
    const lng = boundary.reduce((sum, p) => sum + p.lng, 0) / boundary.length;
    if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
  }
  return null;
}

export function applyStructuredDocSignalsToSed(
  sed: SiteEvidenceDocument,
  docs: readonly SiteDocument[],
): number {
  let writes = 0;
  for (const doc of docs) {
    const iia = doc.iiaResult;
    const extracted = asObject(iia?.extracted);
    if (!iia || !extracted) continue;
    const kind = iia.classification?.kind;
    if (kind === 'survey-plat') {
      const acres = toFiniteNumber(extracted.recordedAcreage);
      if (acres != null) {
        const parcel = ensureSectionData<ParcelEvidence>(sed.parcel, {
          zoning: 'Unknown',
          landUse: 'Unknown',
        });
        if (parcel.acres == null || !Number.isFinite(parcel.acres) || sed.parcel.status === 'unavailable') {
          parcel.acres = acres;
          applyDocLink(sed.parcel, doc.id);
          writes += 1;
        }
      }
      continue;
    }

    if (kind === 'flood-map') {
      const floodZone = toNonEmptyString(extracted.floodZone);
      if (floodZone) {
        const flood = ensureSectionData<FloodEvidence>(sed.flood, {
          floodZone,
          sfha: floodZone.toUpperCase() !== 'X',
          floodRisk: floodRiskFromZone(floodZone),
        });
        if (!flood.floodZone || sed.flood.status === 'unavailable') {
          flood.floodZone = floodZone;
          flood.sfha = floodZone.toUpperCase() !== 'X';
          flood.floodRisk = floodRiskFromZone(floodZone);
          applyDocLink(sed.flood, doc.id);
          writes += 1;
        }
      }
      continue;
    }

    if (kind === 'one-line-diagram') {
      const voltageKv = toFiniteNumber(extracted.voltageKv);
      const capacityMw = toFiniteNumber(extracted.capacityMw);
      const substationName = toNonEmptyString(extracted.substationName);
      const utilityName = toNonEmptyString(extracted.utilityName);
      if (voltageKv == null && capacityMw == null && !substationName && !utilityName) continue;
      const grid = ensureSectionData<GridEvidence>(sed.grid, {
        substationName: substationName ?? 'Unknown',
        substationDistKm: Number.NaN,
        voltage: voltageKv != null ? `${Math.round(voltageKv)} kV` : 'Unknown',
        capacity: capacityMw != null ? gridCapacityFromMw(capacityMw) : 'Limited',
        complexity: 'Medium',
      });
      let wrote = false;
      if (substationName && (!grid.substationName || grid.substationName === 'Unknown')) {
        grid.substationName = substationName;
        wrote = true;
      }
      if (utilityName && !grid.utilityName) {
        grid.utilityName = utilityName;
        wrote = true;
      }
      if (voltageKv != null && (!grid.voltage || grid.voltage === 'Unknown')) {
        grid.voltage = `${Math.round(voltageKv)} kV`;
        wrote = true;
      }
      if (capacityMw != null && sed.grid.status === 'unavailable') {
        grid.capacity = gridCapacityFromMw(capacityMw);
        wrote = true;
      }
      if (wrote) {
        applyDocLink(sed.grid, doc.id);
        writes += 1;
      }
    }
  }
  return writes;
}

function layerCategoryForKind(kind: string): SiteLayer['category'] {
  if (kind === 'one-line-diagram') return 'infrastructure.electrical';
  if (kind === 'flood-map' || kind === 'borehole-log') return 'constraint.environmental';
  if (kind === 'survey-plat') return 'concept.expansion';
  return 'other';
}

function layerSummary(kind: string, extracted: Record<string, unknown>): string {
  if (kind === 'survey-plat') {
    const acres = toFiniteNumber(extracted.recordedAcreage);
    const parcelId = toNonEmptyString(extracted.parcelId);
    const easements = Array.isArray(extracted.easements) ? extracted.easements.length : 0;
    return [parcelId ? `parcel ${parcelId}` : null, acres != null ? `${acres.toFixed(2)} ac` : null, easements > 0 ? `${easements} easement(s)` : null]
      .filter(Boolean)
      .join(' · ') || 'Survey plat structured extraction';
  }
  if (kind === 'flood-map') {
    const zone = toNonEmptyString(extracted.floodZone);
    const bfe = toFiniteNumber(extracted.bfeFt);
    return [zone ? `zone ${zone}` : null, bfe != null ? `BFE ${bfe} ft` : null]
      .filter(Boolean)
      .join(' · ') || 'Flood map structured extraction';
  }
  if (kind === 'one-line-diagram') {
    const utility = toNonEmptyString(extracted.utilityName);
    const voltage = toFiniteNumber(extracted.voltageKv);
    const cap = toFiniteNumber(extracted.capacityMw);
    return [utility, voltage != null ? `${Math.round(voltage)} kV` : null, cap != null ? `${Math.round(cap)} MW` : null]
      .filter(Boolean)
      .join(' · ') || 'Utility diagram structured extraction';
  }
  if (kind === 'borehole-log') {
    const uscs = toNonEmptyString(extracted.uscsClass);
    const bc = toFiniteNumber(extracted.bearingCapacityKPa);
    return [uscs ? `USCS ${uscs}` : null, bc != null ? `${Math.round(bc)} kPa` : null]
      .filter(Boolean)
      .join(' · ') || 'Borehole log structured extraction';
  }
  return 'Document-derived overlay';
}

export function projectDocumentDerivedLayers(
  site: Pick<SiteEntity, 'location' | 'siteBoundary' | 'siteCentroid'>,
  docs: readonly SiteDocument[],
): SiteLayer[] {
  const anchor = siteAnchor(site);
  if (!anchor) return [];
  const output: SiteLayer[] = [];
  const dedupe = new Set<string>();
  for (const doc of docs) {
    const iia = doc.iiaResult;
    const extracted = asObject(iia?.extracted);
    if (!iia || !extracted) continue;
    const kind = iia.classification?.kind ?? 'other';
    const sourceKey = `${doc.id}:${kind}`;
    if (dedupe.has(sourceKey)) continue;
    dedupe.add(sourceKey);
    const summary = layerSummary(kind, extracted);
    const layer: SiteLayer = {
      id: `doc-derived-${doc.id}-${kind}`.replace(/[^a-zA-Z0-9-_]/g, '-'),
      name: `Doc overlay · ${doc.fileName}`,
      category: layerCategoryForKind(kind),
      geometry: {
        type: 'point',
        coordinates: [{ lat: anchor.lat, lng: anchor.lng }],
      },
      description: summary,
      source: 'doc-derived',
      importedAt: iia.runAt ?? doc.uploadedAt ?? new Date().toISOString(),
      visible: false,
      attributes: {
        docId: doc.id,
        docName: doc.fileName,
        iiaKind: kind,
        docDerived: true,
      },
    };
    layer.contentHash = computeLayerContentHash(layer);
    output.push(layer);
  }
  return output;
}
